/**
 * TaskKanbanFilters - Header com busca expansível e filtros rápidos do Kanban de Tarefas
 * 
 * Reutiliza componentes padronizados do sistema:
 * - CrmUserFilter para Responsável e Criado por
 * - Select para Status, Prioridade, Classificação
 * - DateFilterRow para filtros de data
 * - Sheet lateral para filtros avançados ("Mais")
 * 
 * @version 5.3.1
 * RELEASE: 2026-10-06 v5.3.1 - Adicionado separador visual após a busca e melhoria no contraste.
 */

import { useState, useEffect, useMemo } from 'react';
import { Search, X, ClipboardList, RefreshCw, Plus, List, CalendarDays, SlidersHorizontal, PlayCircle, AlertTriangle, Tag, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useTaskModule } from '@/hooks/useTaskModuleType';
import { cn } from '@/lib/utils';
import { DateFilterRow } from '@/components/kanban/KanbanDateFilterRow';
import { KanbanFilterLayout } from '@/components/kanban/KanbanFilterLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PriorityFlag, PRIORITY_LABELS } from '@/components/tasks/PriorityFlag';
import { CrmUserFilter } from '@/components/crm/filters/CrmUserFilter';
import { useTaskClassifications } from '@/hooks/useTaskClassifications';
import { DueDateQuickFilter, type DueDatePreset } from '@/components/tasks/filters/DueDateQuickFilter';
import { RETENTION_PERIOD_OPTIONS } from '@/lib/tasks/retention-period';
import type { TaskKanbanFilters as FilterType } from '@/hooks/useTaskKanban';

const toDateParam = (d: Date | undefined) => (d ? format(d, 'yyyy-MM-dd') : undefined);
const PRIORITY_KEYS = ['urgent', 'high', 'normal', 'low'] as const;

interface TaskKanbanFiltersProps {
  categoryName: string;
  filters: FilterType;
  onFiltersChange: (filters: FilterType) => void;
  onNewTask?: () => void;
  onRefresh?: () => void;
  onToggleView?: () => void;
  className?: string;
  categoryId?: string;
  titleSlot?: React.ReactNode;
}

export function TaskKanbanFilters({
  categoryName,
  filters,
  onFiltersChange,
  onNewTask,
  onRefresh,
  onToggleView,
  className,
  categoryId,
  titleSlot,
}: TaskKanbanFiltersProps) {
  const taskModule = useTaskModule();
  const [isSearchExpanded, setIsSearchExpanded] = useState(() => Boolean(filters.search));
  const [searchValue, setSearchValue] = useState(filters.search || '');
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const [dueDateFrom, setDueDateFrom] = useState<Date | undefined>(
    filters.due_date_from ? new Date(filters.due_date_from) : undefined
  );
  const [dueDateTo, setDueDateTo] = useState<Date | undefined>(
    filters.due_date_to ? new Date(filters.due_date_to) : undefined
  );
  const [duePreset, setDuePreset] = useState<DueDatePreset>(null);
  const [createdFrom, setCreatedFrom] = useState<Date | undefined>(
    filters.created_from ? new Date(filters.created_from) : undefined
  );
  const [createdTo, setCreatedTo] = useState<Date | undefined>(
    filters.created_to ? new Date(filters.created_to) : undefined
  );
  const [completedFrom, setCompletedFrom] = useState<Date | undefined>(
    filters.completed_from ? new Date(filters.completed_from) : undefined
  );
  const [completedTo, setCompletedTo] = useState<Date | undefined>(
    filters.completed_to ? new Date(filters.completed_to) : undefined
  );
  
  const isRetentionOrLegal = taskModule.type === 'retention' || taskModule.type === 'legal';
  const isLegal = taskModule.type === 'legal';
  const [requestFrom, setRequestFrom] = useState<Date | undefined>(
    filters.request_from ? new Date(filters.request_from) : undefined
  );
  const [requestTo, setRequestTo] = useState<Date | undefined>(
    filters.request_to ? new Date(filters.request_to) : undefined
  );
  const [legalDeadlineFrom, setLegalDeadlineFrom] = useState<Date | undefined>(
    filters.legal_deadline_from ? new Date(filters.legal_deadline_from) : undefined
  );
  const [legalDeadlineTo, setLegalDeadlineTo] = useState<Date | undefined>(
    filters.legal_deadline_to ? new Date(filters.legal_deadline_to) : undefined
  );

  const { classifications } = useTaskClassifications(categoryId, true);

  useEffect(() => {
    if (filters.classification_id) {
      onFiltersChange({ ...filters, classification_id: undefined });
    }
  }, [categoryId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchValue !== filters.search) {
        onFiltersChange({ ...filters, search: searchValue || undefined });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchValue]);

  useEffect(() => {
    if (filters.search !== searchValue) {
      setSearchValue(filters.search || '');
    }
  }, [filters.search]);

  const handleSearchBlur = () => {
    if (!searchValue) {
      setIsSearchExpanded(false);
    }
  };

  const toggleSearch = () => {
    if (isSearchExpanded && !searchValue) {
      setIsSearchExpanded(false);
    } else {
      setIsSearchExpanded(true);
    }
  };

  const totalFilterCount = useMemo(() => {
    let count = 0;
    if (filters.status) count++;
    if (filters.priority) count++;
    if (filters.assigned_to) count++;
    if (filters.classification_id) count++;
    if (filters.created_by) count++;
    if (filters.due_date_from || filters.due_date_to || filters.due_date_is_null) count++;
    if (filters.created_from || filters.created_to) count++;
    if (filters.completed_from || filters.completed_to) count++;
    if (filters.period) count++;
    if (filters.request_from || filters.request_to) count++;
    if (filters.legal_deadline_from || filters.legal_deadline_to) count++;
    return count;
  }, [filters]);

  const advancedFiltersCount = useMemo(() => {
    return [
      filters.classification_id,
      filters.created_from || filters.created_to,
      filters.completed_from || filters.completed_to,
      filters.period,
      filters.request_from || filters.request_to,
      filters.legal_deadline_from || filters.legal_deadline_to,
    ].filter(Boolean).length;
  }, [filters]);

  const handleClearAllFilters = () => {
    setDueDateFrom(undefined);
    setDueDateTo(undefined);
    setDuePreset(null);
    setCreatedFrom(undefined);
    setCreatedTo(undefined);
    setCompletedFrom(undefined);
    setCompletedTo(undefined);
    setRequestFrom(undefined);
    setRequestTo(undefined);
    setLegalDeadlineFrom(undefined);
    setLegalDeadlineTo(undefined);
    onFiltersChange({ search: filters.search });
  };

  const updateFilter = (key: keyof FilterType, value: string | undefined) => {
    onFiltersChange({ ...filters, [key]: value || undefined });
  };

  const updateDateFilter = (key: keyof FilterType, date: Date | undefined) => {
    onFiltersChange({ ...filters, [key]: date?.toISOString() || undefined });
  };

  const updateDateOnlyFilter = (key: keyof FilterType, date: Date | undefined) => {
    onFiltersChange({ ...filters, [key]: toDateParam(date) });
  };

  return (
    <KanbanFilterLayout className={className}
      title={
        titleSlot ? (
          <div className="flex items-center shrink-0">{titleSlot}</div>
        ) : (
          <div className="flex items-center gap-2 shrink-0">
            <ClipboardList className="h-5 w-5 text-primary" />
            <h1 className="text-lg font-semibold truncate max-w-[80px] sm:max-w-[160px]">
              {categoryName || taskModule.label}
            </h1>
          </div>
        )
      }
      search={
        <div className="flex items-center shrink-0">
          <div
            className={cn(
              "flex items-center overflow-hidden transition-all duration-300 ease-out",
              isSearchExpanded ? "w-32 sm:w-44 opacity-100 mr-1" : "w-0 opacity-0"
            )}
          >
            <div className="relative w-full">
              <Input
                placeholder="Buscar..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onBlur={handleSearchBlur}
                className="h-7 w-full text-xs rounded-full pl-3 pr-7"
                autoFocus={isSearchExpanded}
              />
              {searchValue && (
                <button
                  onClick={() => { setSearchValue(''); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 rounded-full shrink-0"
            onClick={toggleSearch}
          >
            {isSearchExpanded ? <X className="h-4 w-4" /> : <Search className="h-4 w-4" />}
          </Button>
        </div>
      }
      filters={
        <>
          <Select
            value={filters.status || 'all'}
            onValueChange={(v) => updateFilter('status', v === 'all' ? undefined : v)}
          >
            <SelectTrigger className="h-[22px] w-auto min-w-[100px] text-xs px-2 border-0 bg-background rounded-full">
              <div className="flex items-center gap-1.5">
                <PlayCircle className="h-3 w-3 shrink-0 text-muted-foreground" />
                <SelectValue placeholder="Status" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Status</SelectItem>
              <SelectItem value="pending">Pendente</SelectItem>
              <SelectItem value="in_progress">Andamento</SelectItem>
              <SelectItem value="paused">Pausada</SelectItem>
              <SelectItem value="completed">Concluída</SelectItem>
              <SelectItem value="cancelled">Cancelada</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={filters.priority || 'all'}
            onValueChange={(v) => updateFilter('priority', v === 'all' ? undefined : v)}
          >
            <SelectTrigger className="h-[22px] w-auto min-w-[110px] text-xs px-2 border-0 bg-background rounded-full">
              <div className="flex items-center gap-1.5">
                <AlertTriangle className="h-3 w-3 shrink-0 text-muted-foreground" />
                <SelectValue placeholder="Prioridade" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Prioridade</SelectItem>
              {PRIORITY_KEYS.map((p) => (
                <SelectItem key={p} value={p}>
                  <span className="flex items-center gap-2">
                    <PriorityFlag priority={p} size={13} />
                    {PRIORITY_LABELS[p]}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <DueDateQuickFilter
            from={dueDateFrom}
            to={dueDateTo}
            noDueDate={!!filters.due_date_is_null}
            preset={duePreset}
            triggerClassName="h-[22px] px-2 text-xs border-0 bg-background rounded-full"
            onChange={({ from, to, noDueDate, preset }) => {
              setDueDateFrom(from);
              setDueDateTo(to);
              setDuePreset(preset);
              onFiltersChange({
                ...filters,
                due_date_from: from ? from.toISOString() : undefined,
                due_date_to: to ? to.toISOString() : undefined,
                due_date_is_null: noDueDate ? true : undefined,
              });
            }}
          />

          <CrmUserFilter
            value={filters.assigned_to || null}
            onChange={(id) => updateFilter('assigned_to', id || undefined)}
            label="Responsável"
            searchPlaceholder="Buscar responsável..."
            allLabel="Todos Responsáveis"
            groupLabel="Responsáveis"
            className="h-[22px] text-xs px-2"
          />

          <CrmUserFilter
            value={filters.created_by || null}
            onChange={(id) => updateFilter('created_by', id || undefined)}
            label="Criado por"
            searchPlaceholder="Buscar criador..."
            allLabel="Todos Criadores"
            groupLabel="Criadores"
            className="h-[22px] text-xs px-2"
          />

          <Button
            variant="outline"
            size="sm"
            className="h-[22px] px-2 text-xs gap-1.5 border-0 bg-background rounded-full"
            onClick={() => setIsSheetOpen(true)}
          >
            <SlidersHorizontal className="h-3 w-3" />
            Mais
            {advancedFiltersCount > 0 && (
              <Badge variant="secondary" className="h-4 min-w-[16px] px-1 text-[10px] rounded-full bg-primary/15 text-primary border-0">
                {advancedFiltersCount}
              </Badge>
            )}
          </Button>

          {totalFilterCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="h-[22px] px-2 text-xs gap-1 border-0 bg-background rounded-full text-muted-foreground hover:text-destructive"
              onClick={handleClearAllFilters}
              title="Limpar todos os filtros"
            >
              <X className="h-3.5 w-3.5" />
              <Badge variant="secondary" className="h-4 px-1 text-[10px]">
                {totalFilterCount}
              </Badge>
            </Button>
          )}
        </>
      }
      actions={
        <>
          {onToggleView && (
            <Button
              variant="outline"
              size="icon-sm"
              onClick={onToggleView}
              title="Alternar para listagem"
            >
              <List className="h-3.5 w-3.5" />
            </Button>
          )}
          {onRefresh && (
            <Button
              variant="outline"
              size="icon-sm"
              onClick={onRefresh}
              title="Atualizar dados"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          )}
          {onNewTask && (
            <Button
              size="sm"
              className="h-7 px-2 sm:px-3"
              onClick={onNewTask}
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline ml-1">{taskModule.newLabel}</span>
            </Button>
          )}
        </>
      }
    >
      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent side="right" className="w-80 p-0 flex flex-col">
          <SheetHeader className="p-4 border-b">
            <div className="flex items-center justify-between">
              <SheetTitle className="text-sm">Filtros Avançados</SheetTitle>
              {advancedFiltersCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-muted-foreground"
                  onClick={() => {
                    setCreatedFrom(undefined); setCreatedTo(undefined);
                    setCompletedFrom(undefined); setCompletedTo(undefined);
                    setRequestFrom(undefined); setRequestTo(undefined);
                    setLegalDeadlineFrom(undefined); setLegalDeadlineTo(undefined);
                    onFiltersChange({
                      ...filters,
                      classification_id: undefined,
                      created_from: undefined,
                      created_to: undefined,
                      completed_from: undefined,
                      completed_to: undefined,
                      period: undefined,
                      request_from: undefined,
                      request_to: undefined,
                      legal_deadline_from: undefined,
                      legal_deadline_to: undefined,
                    });
                  }}
                >
                  <X className="h-3 w-3 mr-1" />
                  Limpar
                </Button>
              )}
            </div>
          </SheetHeader>
          <ScrollArea className="flex-1 p-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs">Classificação</Label>
                <Select
                  value={filters.classification_id || 'all'}
                  onValueChange={(v) => updateFilter('classification_id', v === 'all' ? undefined : v)}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {classifications.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs">Período de Criação</Label>
                <DateFilterRow
                  fromDate={createdFrom}
                  toDate={createdTo}
                  onFromChange={(d) => { setCreatedFrom(d); updateDateFilter('created_from', d); }}
                  onToChange={(d) => { setCreatedTo(d); updateDateFilter('created_to', d); }}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs">Período de Conclusão</Label>
                <DateFilterRow
                  fromDate={completedFrom}
                  toDate={completedTo}
                  onFromChange={(d) => { setCompletedFrom(d); updateDateFilter('completed_from', d); }}
                  onToChange={(d) => { setCompletedTo(d); updateDateFilter('completed_to', d); }}
                />
              </div>

              {isRetentionOrLegal && (
                <>
                  <div className="space-y-2">
                    <Label className="text-xs">Tempo em Carteira (Período)</Label>
                    <Select
                      value={filters.period || 'all'}
                      onValueChange={(v) => updateFilter('period', v === 'all' ? undefined : v)}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Qualquer tempo</SelectItem>
                        {RETENTION_PERIOD_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs">Data de {isLegal ? 'Entrada' : 'Solicitação'}</Label>
                    <DateFilterRow
                      fromDate={requestFrom}
                      toDate={requestTo}
                      onFromChange={(d) => { setRequestFrom(d); updateDateOnlyFilter('request_from', d); }}
                      onToChange={(d) => { setRequestTo(d); updateDateOnlyFilter('request_to', d); }}
                    />
                  </div>
                </>
              )}

              {isLegal && (
                <div className="space-y-2">
                  <Label className="text-xs">Prazo Processual</Label>
                  <DateFilterRow
                    fromDate={legalDeadlineFrom}
                    toDate={legalDeadlineTo}
                    onFromChange={(d) => { setLegalDeadlineFrom(d); updateDateOnlyFilter('legal_deadline_from', d); }}
                    onToChange={(d) => { setLegalDeadlineTo(d); updateDateOnlyFilter('legal_deadline_to', d); }}
                  />
                </div>
              )}
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>
    </KanbanFilterLayout>
  );
}
